import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV = [
  { label: "Mission", href: "#mission" },
  { label: "What's Inside", href: "#whats-inside" },
  { label: "The Cards", href: "#cards" },
  { label: "Tiers", href: "#tiers" },
  { label: "Intel", href: "#faq" },
];

export const Navbar = ({ onReserve }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-sf-bg/85 backdrop-blur-md border-b border-sf-border" : "bg-transparent"
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="navbar-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="sf-stencil text-lg md:text-xl tracking-[0.18em] text-sf-bone flex items-center gap-2"
        >
          <span className="text-sf-ember">/</span>SACRED FRONTLINE
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => go(n.href)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-sf-dim hover:text-sf-bone transition-colors"
            >
              {n.label}
            </button>
          ))}
          <motion.button
            data-testid="navbar-reserve-button"
            onClick={onReserve}
            animate={{
              boxShadow: [
                "0 0 0px rgba(210,84,51,0.0)",
                "0 0 18px rgba(210,84,51,0.6)",
                "0 0 0px rgba(210,84,51,0.0)",
              ],
            }}
            transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className="bg-gradient-to-r from-sf-emberDeep to-sf-emberBright text-white font-stencil uppercase tracking-widest text-sm px-6 py-2.5 rounded-sm"
          >
            Reserve My Deck
          </motion.button>
        </nav>

        <button
          data-testid="navbar-mobile-toggle"
          className="md:hidden text-sf-bone"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-sf-bg/95 backdrop-blur-md border-b border-sf-border"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {NAV.map((n) => (
                <button
                  key={n.href}
                  onClick={() => go(n.href)}
                  className="text-left font-mono text-sm uppercase tracking-[0.2em] text-sf-dim hover:text-sf-bone"
                >
                  {n.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onReserve();
                }}
                className="bg-gradient-to-r from-sf-emberDeep to-sf-emberBright text-white font-stencil uppercase tracking-widest text-sm px-5 py-3 rounded-sm"
              >
                Reserve My Deck
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
